import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CurrentPlan from '@/components/subscription/CurrentPlan';
import UsageStats from '@/components/subscription/UsageStats';
import PlanComparison from '@/components/subscription/PlanComparison';
import Button from '@/components/common/Button';
import { createPortalSession } from '@/api/subscriptions';
import { useCurrentSubscription } from '@/hooks/useSubscription';

export default function SubscriptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notification, setNotification] = useState<{
    type: 'success' | 'info';
    message: string;
  } | null>(null);
  const { data: subscription } = useCurrentSubscription();

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setNotification({
        type: 'success',
        message: 'Your subscription has been activated! Welcome aboard.',
      });
      setSearchParams({}, { replace: true });
    } else if (searchParams.get('cancelled') === 'true') {
      setNotification({
        type: 'info',
        message: 'Checkout was cancelled. No changes were made.',
      });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleManageBilling = async () => {
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    }
  };

  const showBillingButton = subscription?.has_billing;

  return (
    <div className="mx-auto max-w-4xl p-6">
      {notification && (
        <div
          className={`mb-6 rounded-lg p-4 ${
            notification.type === 'success'
              ? 'border border-success-200 bg-success-50 text-success-800'
              : 'border border-primary-200 bg-primary-50 text-primary-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="text-sm opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-900">Subscription</h1>
        {showBillingButton && (
          <Button variant="secondary" onClick={handleManageBilling}>
            Manage Billing
          </Button>
        )}
      </div>

      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <CurrentPlan />
          <UsageStats />
        </div>
        <PlanComparison />
      </div>
    </div>
  );
}
